import { registerRootComponent } from 'expo'
import App from './App'

const FIRESTORE_BASE_URL =
	'https://firestore.googleapis.com/v1/projects/nextgenkitchens-e9608/databases/(default)/documents'

registerRootComponent(App)

// Generic fetch function for Firestore
const fetchFirestore = async endpoint => {
	const response = await fetch(`${FIRESTORE_BASE_URL}${endpoint}`, {
		method: 'GET',
		headers: { 'Content-Type': 'application/json' },
	})
	return response.json()
}

// Extract nested value safely
const getNestedValue = (obj, path) => path.reduce((current, key) => current?.[key], obj)

// Get title from SEO data
const extractTitle = (data, fallback) =>
	getNestedValue(data, ['fields', 'seo', 'mapValue', 'fields', 'metaTitle', 'stringValue']) ||
	fallback
const extractDescription = (data, fallback) =>
	getNestedValue(data, ['fields', 'seo', 'mapValue', 'fields', 'metaDescription', 'stringValue']) ||
	fallback
const extractWebUrl = data => getNestedValue(data, ['fields', 'webURL', 'stringValue'])

// Set document title if available
const setTitle = title => title && (document.title = title)
const setMetaTag = (metaName, value) => {
	const meta = document.createElement('meta')
	meta.name = metaName
	meta.content = value
	document.head.appendChild(meta)
}
const setHeadLink = (rel, value) => {
	const link = document.createElement('link')
	link.rel = rel
	link.href = value
	document.head.appendChild(link)
}

// Get restaurant ID from organization slugs
const getRestaurantId = (orgResult, slug) =>
	getNestedValue(orgResult, ['fields', 'slugs', 'mapValue', 'fields', slug, 'stringValue'])

const convertToTitleCase = str => {
	return str.replace(/-/g, ' ').replace(/\b\w/g, char => char.toUpperCase())
}

// Main SEO content logic
const getSEOContent = async () => {
	try {
		const url = window.location.href
		const pathSegments = window.location.pathname.split('/')
		const orgResult = await fetchFirestore(`/Organizations-v2/${global.org}`)
		const orgName = orgResult.fields.name.stringValue

		const titleConfigs = [
			{
				condition: () => url.includes('/menu') && url.includes('token'),
				action: async () => {
					const restaurantSlug = pathSegments[pathSegments.length - 2]
					const itemSlug = pathSegments[pathSegments.length - 1]
					const token = new URL(url).searchParams.get('token')
					const restaurantId = getRestaurantId(orgResult, restaurantSlug)
					const restaurantData = restaurantId
						? await fetchFirestore(`/Restaurants-v2/${restaurantId}`)
						: null
					const itemData = await fetchFirestore(
						`/Restaurants-v2/${restaurantId}/MenuItems/${token}`
					)
					const restaurantName =
						restaurantData?.fields?.name.stringValue || convertToTitleCase(restaurantSlug)

					return {
						item: itemData,
						canonical: `menu/${restaurantSlug}/${itemSlug}`,
						titleFallback: `${restaurantName} - Menu Item | ${orgName}`,
						descriptionFallback: `View menu item details at ${restaurantName} - ${orgName}`,
					}
				},
			},
			{
				condition: () => url.includes('/menu') && !url.includes('token'),
				action: async () => {
					const restaurantSlug = pathSegments[pathSegments.length - 1]
					const restaurantId = getRestaurantId(orgResult, restaurantSlug)
					const restaurantData = restaurantId
						? await fetchFirestore(`/Restaurants-v2/${restaurantId}`)
						: null
					const restaurantName =
						restaurantData?.fields?.name.stringValue || convertToTitleCase(restaurantSlug)

					return {
						item: restaurantData,
						canonical: `menu/${restaurantSlug}`,
						titleFallback: `${restaurantName} - Menu | ${orgName}`,
						descriptionFallback: `View the full menu at ${restaurantName} - ${orgName}`,
					}
				},
			},
			{
				condition: () => true, // Default case
				action: () => {
					return {
						item: orgResult,
						canonical: `home`,
						titleFallback: `${orgName} - Restaurant Selection`,
						descriptionFallback: `Browse and select from our collection of restaurants at ${orgName}`,
					}
				},
			},
		]

		const config = titleConfigs.find(({ condition }) => condition())
		const { item, canonical, titleFallback, descriptionFallback } = await config.action()

		const org = await fetchFirestore(`/Organizations-v2/${global.org}`)

		const fullCanonical = `${extractWebUrl(org)}/${canonical}`
		setTitle(extractTitle(item, titleFallback))
		setMetaTag('description', extractDescription(item, descriptionFallback))
		setHeadLink('canonical', fullCanonical)
	} catch (error) {
		console.error('Error setting document title:', error)
		// Set fallback values on error
		setTitle('NextGen Kitchens')
		setMetaTag('description', 'Order delicious food online')
		setHeadLink('canonical', 'https://nextgenkitchens.com/home')
	}
}

window.onload = getSEOContent
